import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Badge from '@mui/material/Badge';
import IconButton from '@mui/material/IconButton';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { IconInbox } from '@tabler/icons-react';
import { HubConnectionBuilder } from '@microsoft/signalr';
import useAuth from 'hooks/useAuth';
import { refreshNotifications, setNotificationRead, useNotificationCount, useNotifications } from 'api/notifications';
import { refreshExports } from 'api/exports';
import NotificationItem from 'ui-component/notifications/NotificationItem';
import useTenantSelection from 'hooks/useTenantSelection';

export default function ReportInbox() {
  const { user } = useAuth();
  const { data: count } = useNotificationCount();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const { data, error, isLoading } = useNotifications({ readState: 'all', page: 1, pageSize: 5 }, !!anchor);
  const { setTenantId } = useTenantSelection();
  const navigate = useNavigate();
  useEffect(() => {
    if (!user?.id) return;
    const base = window.appConfig?.apiUrl || 'http://localhost:3010/';
    const hub = new HubConnectionBuilder()
      .withUrl(new URL('hubs/report-notifications', base.endsWith('/') ? base : base + '/').toString(), {
        accessTokenFactory: () => localStorage.getItem('serviceToken') ?? ''
      })
      .withAutomaticReconnect()
      .build();
    const refresh = () => {
      void refreshNotifications();
      void refreshExports();
    };
    hub.on('ReportsChanged', refresh);
    hub.onreconnected(refresh);
    let active = true;
    const start = async () => {
      try {
        await hub.start();
        if (active) refresh();
        else await hub.stop();
      } catch {
        if (active) retry = window.setTimeout(() => void start(), 30_000);
      }
    };
    let retry: number | undefined;
    hub.onclose(() => {
      if (active) {
        window.clearTimeout(retry);
        retry = window.setTimeout(() => void start(), 30_000);
      }
    });
    void start();
    return () => {
      active = false;
      window.clearTimeout(retry);
      void hub.stop();
    };
  }, [user?.id]);
  return (
    <>
      <IconButton
        onClick={(event) => setAnchor(event.currentTarget)}
        aria-label={`Inbox, ${count ?? 0} unread notifications`}
        aria-haspopup="dialog"
        aria-expanded={!!anchor}
        aria-controls={anchor ? 'report-inbox' : undefined}
      >
        <Badge badgeContent={count ?? 0} color="error">
          <IconInbox />
        </Badge>
      </IconButton>
      <Popover
        id="report-inbox"
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Stack
          spacing={1}
          sx={{ p: 2, width: { xs: 'calc(100vw - 48px)', sm: 420 }, maxWidth: 420 }}
          role="dialog"
          aria-label="Recent notifications"
        >
          <Typography variant="h4">Notifications</Typography>
          {isLoading && <Typography>Loading notifications…</Typography>}
          {error && <Alert severity="error">Could not load notifications.</Alert>}
          {!isLoading && !error && !data?.items.length && <Typography>No recent notifications.</Typography>}
          {data?.items.map((item) => (
            <NotificationItem
              key={item.notificationId}
              item={item}
              onDetails={() => {
                if (!item.readAt) void setNotificationRead(item.notificationId, true, item.tenantId).catch(() => {});
                setAnchor(null);
                if (item.relatedExportId || item.relatedScheduleId) {
                  // Another tab may have switched the shared tenant; open the target in the notification's tenant.
                  setTenantId(item.tenantId);
                  navigate(
                    item.relatedExportId
                      ? `/my-reports?tab=my-exports&exportId=${item.relatedExportId}`
                      : `/admin?tab=report-schedules&scheduleId=${item.relatedScheduleId}`
                  );
                } else navigate('/notifications');
              }}
            />
          ))}
          <Button component={Link} to="/notifications" onClick={() => setAnchor(null)}>
            View all notifications
          </Button>
        </Stack>
      </Popover>
    </>
  );
}
